import React, { FC, useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  IDownloadNotificationAttachmentDTO,
  INotificationMessageDTO,
  INotificationUserEmployeeDTO,
} from 'src/api/dtos';
import NotificationsRepository from 'src/api/repositories/common/v1/notifications.repository';
import { IError } from 'src/api/types';
import { NotificationMessageTypeEnums } from 'src/features/message-center/MessageCenter.enums';
import { formatUserEmployeeName, VISIBLE_RECIPIENTS_COUNT } from 'src/features/message-center/MessageCenter.utils';
import { useMessageCenterEdit, useMessageCenterQuery } from 'src/features/message-center/providers';
import {
  ChevronDownIcon,
  CloseIcon,
  DeleteIcon,
  FileChip,
  ForwardIcon,
  MailDotIcon,
  ReplyAllIcon,
  ReplyIcon,
} from 'src/shared/components';
import { DAY_DATE_MONTH_FORMAT } from 'src/shared/constants';
import { MailboxFolderEnums, PermissionsEnums } from 'src/shared/enums';
import { ComponentWithPermissions } from 'src/shared/hocs';
import { useConfirmationSnackBar, useTranslate } from 'src/shared/providers';
import { formatDateTime, getErrorMessage, handleScrollEnd, saveAttachment } from 'src/shared/utils';
import { getTimeFormat } from 'src/store/user/user.selectors';

const SCROLL_OFFSET = 100;

interface Props {
  notificationMessage: INotificationMessageDTO;
  recipients: INotificationUserEmployeeDTO[];
  recipientsCount: number;
  loading: boolean;
  onClose: () => void;
  onLoadMore: () => void;
}

export const MessageCenterDetailsRead: FC<Props> = ({
  notificationMessage,
  recipients,
  recipientsCount,
  loading,
  onClose,
  onLoadMore,
}) => {
  const { t } = useTranslate();
  const snackBar = useConfirmationSnackBar();
  const timeFormat = useSelector(getTimeFormat);

  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [downloadIndex, setDownloadIndex] = useState<number | null>(null);

  const { onEditMessage } = useMessageCenterEdit();
  const { filters } = useMessageCenterQuery();

  const { id, fromUser, date, subject, message } = notificationMessage;

  const [updateNotifications, { isLoading: isUpdating }] =
    NotificationsRepository.endpoints.useUpdateNotificationsMutation();

  const [deleteNotifications, { isLoading: isDeleting }] =
    NotificationsRepository.endpoints.useDeleteNotificationsMutation();

  const mutating = isUpdating || isDeleting;

  const handleMarkAsUnread = async () => {
    try {
      await updateNotifications({ ids: [id], read: false }).unwrap();

      toast.info(t('messageCenter.youHaveSuccessfullyMarkedMessageUnread'), {
        type: toast.TYPE.SUCCESS,
      });
    } catch (error) {
      const errorMessage = getErrorMessage(error as IError, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
    }
  };

  const handleDelete = () => {
    const submit = async () => {
      try {
        await deleteNotifications({ ids: [id] }).unwrap();

        toast.info(t('messageCenter.youHaveSuccessfullyDeletedMessage'), {
          type: toast.TYPE.SUCCESS,
        });

        onClose();
      } catch (error) {
        const errorMessage = getErrorMessage(error as IError, t('global.oopsAnErrorOccurred'));
        toast.info(errorMessage, { type: toast.TYPE.ERROR });
      }
    };

    snackBar.show(
      {
        confirmation: t('messageCenter.areYouSureWantToDeleteMessage'),
        undo: t('global.actionUndone'),
      },
      submit,
    );
  };

  const downloadAttachment = async (attachmentId: number, fileName?: string) => {
    try {
      setIsDownloading(true);
      const payload: IDownloadNotificationAttachmentDTO = {
        notificationId: notificationMessage.id,
        attachmentId,
      };
      const repo = new NotificationsRepository();
      const { stream } = await repo.download(payload);

      if (!stream) {
        toast.info(t('global.unableToDownload'), { type: toast.TYPE.ERROR });
        return;
      }

      if (stream) {
        saveAttachment(stream, fileName || '');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Paper
      sx={{
        padding: 2,
        width: '100%',
        alignSelf: 'flex-start',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflow: 'hidden',
        maxHeight: '100%',
      }}
    >
      <Stack direction="row" gap={2} justifyContent="space-between">
        <Typography variant="h4" fontSize={24} fontWeight={500}>
          {subject}
        </Typography>
        <Button
          size="small"
          variant="contained"
          color="info"
          onClick={onClose}
          sx={{ width: 40, height: 40, minWidth: 'unset' }}
        >
          <CloseIcon />
        </Button>
      </Stack>
      <Grid container alignItems="center">
        <Grid item xs={1}>
          <Typography variant="body2" color="text.secondary">
            {t('messageCenter.from')}:
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body2">{formatUserEmployeeName(fromUser)}</Typography>
        </Grid>
        <Grid item xs={5} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {formatDateTime(date, DAY_DATE_MONTH_FORMAT, timeFormat)}
          </Typography>
          <ComponentWithPermissions permissions={PermissionsEnums.NOTIFICATIONS_EDIT}>
            {filters.mailboxFolder === MailboxFolderEnums.IN && (
              <LoadingButton
                variant="text"
                sx={{ fontSize: '0.875rem' }}
                onClick={handleMarkAsUnread}
                disabled={mutating}
                loading={isUpdating}
                startIcon={<MailDotIcon size={20} />}
              >
                {t('messageCenter.markAsUnread')}
              </LoadingButton>
            )}
          </ComponentWithPermissions>
        </Grid>
        <Grid item xs={1}>
          <Typography variant="body2" color="text.secondary">
            {t('messageCenter.to')}:
          </Typography>
        </Grid>
        <Grid item xs={11}>
          <Stack direction="row" alignItems="center" gap={1}>
            {recipients.slice(0, VISIBLE_RECIPIENTS_COUNT).map((recipient, index, recipients) => (
              <Typography key={recipient.id} variant="body2">
                {formatUserEmployeeName(recipient)}
                {index !== recipients.length - 1 ? '; ' : ''}
              </Typography>
            ))}
            {recipients.length > VISIBLE_RECIPIENTS_COUNT && (
              <MessageCenterDetailsReadRecipients
                recipients={recipients.slice(VISIBLE_RECIPIENTS_COUNT, recipients.length)}
                recipientsCount={recipientsCount}
                loading={loading}
                onLoadMore={onLoadMore}
              />
            )}
          </Stack>
        </Grid>
      </Grid>
      <Stack justifyContent="flex-start">
        {!!notificationMessage.attachments?.length && (
          <Grid container spacing={2}>
            {notificationMessage.attachments.map((file, index) => (
              <Grid item key={`${file.fileName}-${index}`} xs={notificationMessage?.attachments?.length === 1 ? 12 : 6}>
                <FileChip
                  isDownloading={isDownloading && downloadIndex === index}
                  attachment={file}
                  onDownloadFile={() => {
                    setDownloadIndex(index);
                    downloadAttachment(file.id, file.fileName);
                  }}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>
      <Stack direction="row" overflow="auto">
        <Typography whiteSpace="pre-wrap">{message}</Typography>
      </Stack>
      <ComponentWithPermissions permissions={PermissionsEnums.NOTIFICATIONS_EDIT}>
        <Stack direction="row" gap={1}>
          {filters.mailboxFolder !== MailboxFolderEnums.OUT && (
            <>
              <Button
                variant="text"
                sx={{ fontSize: '0.875rem', lineHeight: '20px' }}
                disabled={loading}
                onClick={() => onEditMessage(notificationMessage, NotificationMessageTypeEnums.REPLY)}
              >
                <ReplyIcon size={20} />
                {t('messageCenter.reply')}
              </Button>
              <Button
                variant="text"
                sx={{ fontSize: '0.875rem', lineHeight: '20px' }}
                disabled={loading}
                onClick={() => onEditMessage(notificationMessage, NotificationMessageTypeEnums.REPLY_ALL)}
              >
                <ReplyAllIcon size={20} />
                {t('messageCenter.replyToAll')}
              </Button>
            </>
          )}
          <Button
            variant="text"
            sx={{ fontSize: '0.875rem', lineHeight: '20px' }}
            disabled={loading}
            onClick={() => onEditMessage(notificationMessage, NotificationMessageTypeEnums.FORWARD)}
          >
            <ForwardIcon size={20} />
            {t('messageCenter.forward')}
          </Button>
          <LoadingButton
            variant="text"
            sx={{ fontSize: '0.875rem', lineHeight: '20px' }}
            color="error"
            onClick={handleDelete}
            disabled={loading}
            loading={isDeleting}
          >
            <DeleteIcon size={20} />
            {t('global.delete')}
          </LoadingButton>
        </Stack>
      </ComponentWithPermissions>
    </Paper>
  );
};

const MessageCenterDetailsReadRecipients: FC<{
  recipients: INotificationUserEmployeeDTO[];
  recipientsCount: number;
  loading: boolean;
  onLoadMore: () => void;
}> = ({ recipients, recipientsCount, loading, onLoadMore }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Stack direction="row" alignItems="center">
        <Typography variant="body2">+{recipientsCount - VISIBLE_RECIPIENTS_COUNT}</Typography>
        <IconButton size="small" onClick={handleOpen} sx={{ flexShrink: 0 }}>
          <ChevronDownIcon />
        </IconButton>
      </Stack>
      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: { width: '100%', maxWidth: 200, maxHeight: 200, overflow: 'auto' },
            onScroll: (event) => handleScrollEnd(event, onLoadMore, { loading, scrollOffset: SCROLL_OFFSET }),
          },
        }}
        sx={{ mt: 1 }}
      >
        <List>
          {recipients.map((recipient) => (
            <ListItem key={recipient.id}>
              <Typography variant="caption">{formatUserEmployeeName(recipient)}</Typography>
            </ListItem>
          ))}
        </List>
      </Popover>
    </>
  );
};
